import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './retro.css';
import { SNIPPIT_API_URL } from '../App';

type Artist = {
  id: string;
  name: string;
  image_small: string;
  image_medium: string;
  image_xl: string;
};

type GameMode = 'streak' | 'quiz';
type PoolSize = 50 | 100 | 500 | 1000;

export function DeezerFooter() {
  return (
    <div className="deezer-footer">
      Powered by <img className="deezer-logo" src="https://media.ffycdn.net/eu/deezer/XHqkMxe2Sry9vCSMgqmB.png?width=2400" alt="Deezer logo" />
    </div>
  )
}

export function Footer() {
  return (
    <div className="footer-row">
      {DeezerFooter()}
      <a href='https://ko-fi.com/I3I1C79UF' target='_blank' rel="noreferrer"><img height='36' style={{ border: '0px', height: '36px' }} src='https://storage.ko-fi.com/cdn/kofi4.png?v=6' alt='Buy Me a Coffee at ko-fi.com' /></a>
    </div>
  )
}

function BetaModal({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="modal-overlay" onClick={onDismiss}>
      <div className="beta-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onDismiss}>✕</button>
        <div className="modal-content">
          <p>Welcome to the Sound Bits beta. This is a working pre-release version that's here to collect feedback!</p>
          <p>For now, explore and enjoy and let me know any feedback!</p>
          <p>The chart game is under construction, any issue reports are welcome but just know it's being improved actively!</p>
        </div>
      </div>
    </div>
  );
}

function InfoModal({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="modal-overlay" onClick={onDismiss}>
      <div className="beta-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onDismiss}>✕</button>
        <div className="modal-content">
          <h3 className="modal-title">About Sound Bits</h3>
          <p>One game I've been annoying about since college is asking someone to play an artist's discography on shuffle while I name the songs I hear as quick as I can.</p>
          <p>I got the idea from <a className="link" href="https://www.youtube.com/watch?v=x8GeoZ97GLs">this video of Jack White doing this with Beatles songs.</a></p>
          <p>I threw Sound Bits together to automate this game so that my buddies can relax without having to navigate to the Radiohead shuffle button ever again.</p>
          <p>In addition to this shuffle mode, there's also a <a className="link" href="/chart-game">daily chart game</a> that pulls a random weekend in Billboard Hot 100 history for you to try and name the top ten.</p>
          <p>The chart game has its kinks as I work through some data cleanup and admin, but I'm happily collecting responses about the experience.</p>
          <p>Have feedback or found a bug? <a className="link" href="mailto:bannigeri17@gmail.com?subject=SOUND%20BITS%20FEEDBACK" target="_blank" rel="noopener noreferrer">Email me here!</a></p>
        </div>
      </div>
    </div>
  );
}

export default function LandingView() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Artist[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [error, setError] = useState('');
  const [selectedMode, setSelectedMode] = useState<GameMode>('quiz');
  const [songCount, setSongCount] = useState(5);
  const [poolSize, setPoolSize] = useState<PoolSize>(50);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem('betaModalDismissed');
    if (!dismissed) {
      setShowBetaModal(true);
    }
  }, []);

  const handleDismissModal = () => {
    setShowBetaModal(false);
    localStorage.setItem('betaModalDismissed', 'true');
  };

  const modeDescriptions: Record<GameMode, string> = {
    quiz: 'Quiz mode is the classic Sound Bits game. You get a random set of songs from an artist, and you name the song you\'re hearing!',
    streak: 'Use streak mode to see how long you can keep naming songs by an artist. But be careful! As soon as you make an incorrect guess, your streak is over!'
  };

  const handleSearch = async () => {
    if (!term.trim()) return;
    setSearching(true);
    setSearchError('');
    setResults([]);

    try {
      const res = await fetch(`${SNIPPIT_API_URL}/api/artists/search/${encodeURIComponent(term.trim())}`);
      if (!res.ok) throw new Error('Search failed');
      const data: Artist[] = await res.json();
      if (data.length === 0) setSearchError('No artists found.');
      setResults(data);
    } catch {
      setSearchError('Search failed. Try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const poolSizeOptions: PoolSize[] = [50, 100, 500, 1000];
  const poolSizeLabels: Record<PoolSize, string> = {
    50: 'Easy',
    100: 'Medium',
    500: 'Difficult',
    1000: 'Everything'
  };

  const poolSizeDescriptions: Record<PoolSize, string> = {
    50: 'The radio hits and big players',
    100: 'Some deeper cuts and fan faves',
    500: 'Ball knower material',
    1000: 'All of the nitty gritty: b-sides and remixes and live cuts and all'
  };

  const handleSelectArtist = async (artist: Artist) => {
    setResults([]);
    setTerm('');
    setError('');

    const count = selectedMode === 'streak' ? 10 : songCount;
    
    navigate('/loading', {
      state: {
        url: `${SNIPPIT_API_URL}/api/snippets/${artist.id}?count=${count}&poolSize=${poolSize}`,
        contextName: artist.name,
        backPath: '/',
        gameMode: selectedMode,
        backgroundImage: artist.image_xl,
        artistId: artist.id,
        poolSize: poolSize
      }
    });
  };

  return (
    <div className="landing-root">
      <div className="grain" />
      <div className="vignette" />

      {showBetaModal && <BetaModal onDismiss={handleDismissModal} />}
      {showInfoModal && <InfoModal onDismiss={() => setShowInfoModal(false)} />}

      <div className="landing-inner">
        <div className="logo-block">
          <div className="logo-eyebrow">♪ now playing ♪</div>
          <h1 className="logo">SOUND BITS</h1>
          <div className="logo-sub">put your ear and music knowledge to the test</div>
          <button className="info-btn" onClick={() => setShowInfoModal(true)}>?</button>
        </div>

        <div className="mode-selector">
          <label className="mode-label">GAME MODE</label>
          <div className="mode-buttons">
            <button
              className={`mode-btn ${selectedMode === 'quiz' ? 'mode-active' : ''}`}
              onClick={() => setSelectedMode('quiz')}
            >
              QUIZ
            </button>
            <button
              className={`mode-btn ${selectedMode === 'streak' ? 'mode-active' : ''}`}
              onClick={() => setSelectedMode('streak')}
            >
              STREAK
            </button>
          </div>
          <p className="mode-description">{modeDescriptions[selectedMode]}</p>
        </div>

        {(
          <div className="pool-size-selector">
            <label className="field-label">DIFFICULTY</label>
            <div className="count-input-group">
              <button
                className="count-btn"
                onClick={() => {
                  const currentIndex = poolSizeOptions.indexOf(poolSize);
                  if (currentIndex > 0) setPoolSize(poolSizeOptions[currentIndex - 1]);
                }}
              >
                −
              </button>
              <div className="count-input">
                {poolSizeLabels[poolSize]}
              </div>
              <button
                className="count-btn"
                onClick={() => {
                  const currentIndex = poolSizeOptions.indexOf(poolSize);
                  if (currentIndex < poolSizeOptions.length - 1) setPoolSize(poolSizeOptions[currentIndex + 1]);
                }}
              >
                +
              </button>
            </div>
            <p className="pool-size-description">{poolSizeDescriptions[poolSize]}</p>
          </div>
        )}

        {selectedMode === 'quiz' && (
          <div className="song-count-selector">
            <label className="field-label">HOW MANY SONGS?</label>
            <div className="count-input-group">
              <button
                className="count-btn"
                onClick={() => setSongCount(Math.max(5, songCount - 5))}
              >
                −
              </button>
              <div
                className="count-input">
                {songCount}
              </div>
              <button
                className="count-btn"
                onClick={() => setSongCount(Math.min(20, songCount + 5))}
              >
                +
              </button>
            </div>
          </div>
        )}

        <div className="tape-deck">
          <div className="reel reel-left">
            <div className="reel-hub" />
          </div>
          <div className="tape-label">
            <div className="tape-label-inner">
              <div className="field-group">
                <label className="field-label">SEARCH BY ARTIST</label>
                <div className="search-row">
                  <input
                    className="retro-input"
                    type="text"
                    placeholder="e.g. Radiohead"
                    value={term}
                    onChange={e => setTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  <button className="search-btn" onClick={handleSearch} disabled={searching}>
                    {searching ? '...' : '⌕'}
                  </button>
                </div>

                {results.length > 0 && (
                  <div className="search-dropdown">
                    {results.map(artist => (
                      <div
                        key={artist.id}
                        className="search-result-item"
                        onClick={() => handleSelectArtist(artist)}
                      >
                        <img className="search-result-thumbnail" src={artist.image_small} alt={`${artist.name} thumbnail`}/>
                        <span>{artist.name}</span>
                      </div>
                    ))}
                  </div>
                )}

                {searchError && <div className="error-msg">{searchError}</div>}
                {error && <div className="error-msg">{error}</div>}
              </div>
            </div>
          </div>
          <div className="reel reel-right">
            <div className="reel-hub" />
          </div>
        </div>

        <div className="mode-divider">
          <span className="divider-line" />
          <span className="divider-text">OR</span>
          <span className="divider-line" />
        </div>

        <button className="stations-mode-btn" onClick={() => navigate('/stations')}>
          ♫ BROWSE RADIO STATIONS
        </button>

        <button className="stations-mode-btn" onClick={() => navigate('/chart-game')}>
          📊 DAILY CHART CHALLENGE
        </button>

        <div className="footer-note">search an artist or play the daily chart game</div>
        {Footer()}
      </div>
    </div>
  );
}